import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import moment from 'moment'
import {
  config
} from './config'

Vue.use(Vuex)

const iconSize = [50, 50]
const iconAnchor = [10, 27]
const iconPopupAnchor = [16, -24]

const clusterIconSize = new L.Point(40, 40)
const clusterShowCoverageOnHover = false

const iconLand = L.icon({
  iconUrl: require('./assets/assets-land.svg'),
  iconSize: iconSize,
  iconAnchor: iconAnchor,
  popupAnchor: iconPopupAnchor
})

const iconProduction = L.icon({
  iconUrl: require('./assets/assets-production.svg'),
  iconSize: iconSize,
  iconAnchor: iconAnchor,
  popupAnchor: iconPopupAnchor
})

const iconRoyalty = L.icon({
  iconUrl: require('./assets/assets-royalty.svg'),
  iconSize: iconSize,
  iconAnchor: iconAnchor,
  popupAnchor: iconPopupAnchor
})

const state = {
  map: null,
  totalFeatures: 0,
  assetData: [],
  snackbar: false,
  snackbarContext: '',
  snackbarTimeout: 6000,
  snackbarText: '',
  assetManager: null,
  assetsLayer: null,
  pipelineLayer: null,
  gridLayer: null,
  dlsLayer: null,
  townshipHighlightLayer: null
}

const mutations = {
  updateMap(state, feature) {
    state.map = feature
  },
  updateAssetManager(state, feature) {
    state.assetManager = feature
  },
  loadAssetsLayer(state, feature) {
    state.assetsLayer = feature
    if (state.totalFeatures > 0) {
      state.map.fitBounds(feature.getBounds())
      state.snackbarContext = 'success'
      let _asset = state.totalFeatures > 9 ? 'assets' : 'asset'
      state.snackbarText = `${state.totalFeatures} ${_asset} were retrieved.`
      state.snackbar = true
      setTimeout(() => {
        state.snackbar = false
      }, state.snackbarTimeout)
    } else {
      state.snackbarContext = 'error'
      state.snackbarText = `No assets were found. Try different filters perhaps.`
      state.snackbar = true
      setTimeout(() => {
        state.snackbar = false
      }, state.snackbarTimeout)

    }
  },
  updatePipelineLayer(state, feature) {
    state.pipelineLayer = feature
  },
  updateGridLayer(state, feature) {
    state.gridLayer = feature
  },
  updateDlsLayer(state, feature) {
    state.dlsLayer = feature
  },
  updateTownshipHighlightLayer(state, feature) {
    state.townshipHighlightLayer = feature
  }
}

const actions = {
  updateTownshipHighlightLayer({
    commit
  }, feature) {
    commit('updateTownshipHighlightLayer', feature)
  },
  loadAssetsLayer({
    commit,
    state
  }, filter) {

    let wfsParams = {
      service: 'WFS',
      version: '2.0.0',
      request: 'GetFeature',
      typeName: 'jwn:assets',
      outputFormat: 'application/json',
      srsname: 'EPSG:4326'
    }

    if (filter !== null) {
      wfsParams.cql_filter = filter
    }

    const icons = {
      'Land': iconLand,
      'Production': iconProduction,
      'Royalty': iconRoyalty
    }

    const highlights = {
      'Land': '#85a800',
      'Production': '#ef5534',
      'Royalty': '#0085b1'
    }

    axios.defaults.withCredentials = true

    axios.get(config.serverUrl, {
        params: wfsParams
      })
      .then(function(response) {
        state.totalFeatures = response.data.totalFeatures
        state.assetData = []
        for (let feature of response.data.features) {
          state.assetData.push({
            area: feature.properties.area,
            asset_type: feature.properties.asset_type,
            company: feature.properties.company,
            bid_date: feature.properties.bid_date !== null ? moment(feature.properties.bid_date).format('YYYY-MM-DD HH:mm:ss') : null,
            agent: feature.properties.agent,
            description: feature.properties.description
          })
        }
        let _assets = []
        for (let asset of ['Land', 'Production', 'Royalty']) {
          let marker = L.geoJson(response.data, {
            filter: function(feature, layer) {
              return feature.properties.asset_type === asset
            },
            pointToLayer: (feature, latlng) => {
              return L.marker(latlng, {
                icon: icons[feature.properties.asset_type]
              })
            },
            onEachFeature: (feature, layer) => {
              if (feature.properties) {
                layer.bindPopup(`<div class="app-popup">
                                    <table>
                                      <tr>
                                        <th>Area</th>
                                        <td>${feature.properties.area}</td>
                                      </tr>
                                      <tr>
                                        <th>Asset Type</th>
                                        <td>${feature.properties.asset_type}</td>
                                      </tr>
                                      <tr>
                                        <th>Advisor</th>
                                        <td>${feature.properties.agent}</td>
                                      </tr>
                                      <tr>
                                        <th>Bid Date</th>
                                        <td>${feature.properties.bid_date !== null ? moment(feature.properties.bid_date).format('YYYY-MM-DD HH:mm:ss') : null}</td>
                                      </tr>
                                      <tr>
                                        <th>Description</th>
                                        <td>${feature.properties.description}</td>
                                      </tr>
                                    </table>
                                    <br/>
                                    Note: The pin represents an average of all locations. Individual locations are highlighted.
                                </div>`)
              }
              layer.on({
                click: function(e) {
                  let areaId = feature.properties.area_id
                  let wfsParams = {
                    service: 'WFS',
                    version: '2.0.0',
                    request: 'GetFeature',
                    typeName: 'jwn:townships',
                    outputFormat: 'application/json',
                    srsname: 'EPSG:4326',
                    cql_filter: `area_id=${areaId}`
                  }
                  axios.get(config.serverUrl, {
                      params: wfsParams
                    })
                    .then(function(response) {
                      state.map.hasLayer(state.townshipHighlightLayer) && state.map.removeLayer(state.townshipHighlightLayer)
                      if (response.data.totalFeatures > 0) {
                        commit('updateTownshipHighlightLayer', L.geoJSON(response.data, {
                          style: {
                            fillOpacity: 0.5,
                            fillColor: highlights[feature.properties.asset_type],
                            color: highlights[feature.properties.asset_type],
                            weight: 1
                          },
                          onEachFeature: (feature, layer) => {
                            if (feature.properties) {
                              layer.bindPopup(feature.properties.dls_location)
                            }
                          }
                        }))
                      }
                    }).catch(function(error) {
                      console.log(error)
                    })
                }
              })
            }
          })
          let markerCluster = L.markerClusterGroup({
            iconCreateFunction: (cluster) => {
              let childCount = cluster.getChildCount()
              return new L.DivIcon({
                html: '<div><span>' + childCount + '</span></div>',
                className: 'marker-cluster marker-cluster-small ' + asset.toLowerCase(),
                iconSize: clusterIconSize
              })
            },
            showCoverageOnHover: clusterShowCoverageOnHover
          })
          markerCluster.addLayer(marker)
          _assets.push(markerCluster)
        }
        commit('loadAssetsLayer', L.featureGroup(_assets))
      })
      .catch(function(error) {
        console.log(error)
        // document.querySelector(".login").style.display = "block"
      })
  }
}

export const store = new Vuex.Store({
  state,
  mutations,
  actions
})



// WEBPACK FOOTER //
// ./src/store.js